import { Component, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Subject, Subscription, takeUntil } from 'rxjs';

import {
  NudgeJobPreviewLink,
  NudgeLibraryPortService,
  NudgePortJob,
  NudgePortNudgeStatus,
} from './nudge-library-port.service';
import { ConfirmationComponent } from '../../modals/confirmation/confirmation.component';
import { getRegion } from '../../core/http/env-utils';

export interface NudgePortJobsDialogData {
  target: string;
  targetName: string;
}

interface JobResult {
  loading: boolean;
  error: string;
  links: NudgeJobPreviewLink[];
}

/**
 * "Library jobs" — active and recent nudge-port runs for one client.
 *
 * This used to be an `<ng-template>` inside `NudgeLibraryComponent`, which made
 * it unopenable from anywhere else. It is now its own component so the sidenav's
 * active-jobs pill can open the very same dialog.
 *
 * It reads the port service's `jobs$` rather than starting its own
 * `watchActiveJobs` listing: that listing polls every 5s, the library page
 * already runs one, and a second would double the traffic for the same data.
 * Live progress still arrives — each running job is polled by the service
 * itself, and every update pushes through `jobs$`. Recent (finished) jobs come
 * from one `getRecentJobs` call on open and on Refresh.
 */
@Component({
  selector: 'app-nudge-port-jobs-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: './nudge-port-jobs-dialog.component.html',
  styleUrls: ['./nudge-port-jobs-dialog.component.css'],
})
export class NudgePortJobsDialogComponent implements OnInit, OnDestroy {
  readonly portService = inject(NudgeLibraryPortService); // `retry` is called from the template
  private readonly dialog = inject(MatDialog);
  private readonly destroy$ = new Subject<void>();
  private requests = new Subscription();
  private deleteConfirmationRef?: MatDialogRef<ConfirmationComponent>;
  private stopConfirmationRef?: MatDialogRef<ConfirmationComponent>;
  private readonly region = getRegion();

  modalJobs: NudgePortJob[] = [];
  modalJobGroups: { label: string; jobs: NudgePortJob[] }[] = [];
  jobResults: Record<string, JobResult | undefined> = {};
  historyLoading = false;
  /** nudge id whose preview link is being fetched */
  testPending = '';
  historyError = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: NudgePortJobsDialogData,
  ) {}

  deletePending = '';
  deleteError = '';
  deleteErrorJobId = '';
  canDeleteJob(job: NudgePortJob): boolean {
    return !job.ready && !job.needsReview &&
      (job.status === 'canceled' || job.status === 'error' || (job.status === 'completed' && job.failed > 0));
  }
  confirmDeleteJob(job: NudgePortJob): void {
    if (this.deleteConfirmationRef || this.deletePending || this.activeCount > 0 || !this.canDeleteJob(job)) return;
    this.deleteConfirmationRef = this.dialog.open(ConfirmationComponent, {
      width: '440px',
      maxWidth: '94vw',
      data: {
        header: 'Delete this build?',
        content: `Are you sure you want to delete this failed or canceled build for ${job.targetName}? Its unused nudges will also be removed from the dashboard. This cannot be undone.`,
        confirm_btn: 'Delete build',
        cancel_btn: 'Cancel',
        confirmButtonDangerStyle: true,
      },
    });
    this.deleteConfirmationRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.deleteConfirmationRef = undefined;
      if (result !== 1) return;
      // Polling may have changed the job while the confirmation was open.
      const current = this.modalJobs.find((item) => item.key === job.key);
      if (current) this.deleteJob(current);
    });
  }

  private deleteJob(job: NudgePortJob): void {
    if (this.deletePending || this.activeCount > 0 || !this.canDeleteJob(job)) return;
    this.deletePending = job.jobId;
    this.deleteError = '';
    this.deleteErrorJobId = '';
    this.portService.deleteFailedJob(job).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { this.deletePending = ''; this.loadJobHistory(); },
      error: (err) => {
        this.deletePending = '';
        this.deleteErrorJobId = job.jobId;
        this.deleteError = err?.error?.message || err?.message || 'Could not delete this build.';
      },
    });
  }

  retryPending = '';
  retryError = '';
  retryNudge(job: NudgePortJob, nudge: NudgePortNudgeStatus): void {
    if(this.retryPending) return;
    this.retryPending=job.jobId+':'+nudge.sourceIncentiveID;this.retryError='';
    this.portService.retryNudge(job,nudge.sourceTemplateId || nudge.sourceIncentiveID)
      .pipe(takeUntil(this.destroy$)).subscribe({next:()=>{this.retryPending='';this.loadJobHistory();},error:err=>{this.retryPending='';this.retryError=err?.error?.message || err?.message || 'Could not retry this nudge.';}});
  }

  get activeCount(): number {
    return this.modalJobs.filter((job) => job.status === 'running').length;
  }

  ngOnInit(): void {
    this.portService.jobs$.pipe(takeUntil(this.destroy$)).subscribe((jobs) => {
      this.modalJobs = jobs
        .filter(
          (job) => job.target === this.data.target && job.region === this.region,
        )
        .sort(
          (a, b) =>
            (Date.parse(b.createdAt || '') || 0) -
            (Date.parse(a.createdAt || '') || 0),
        );
      this.modalJobGroups = [
        {
          label: 'Active jobs',
          jobs: this.modalJobs.filter((job) => job.status === 'running'),
        },
        {
          label: 'Finished jobs',
          jobs: this.modalJobs.filter((job) => job.status !== 'running'),
        },
      ].filter((group) => group.jobs.length);
    });
    this.loadJobHistory();
  }

  ngOnDestroy(): void {
    this.deleteConfirmationRef?.close();
    this.stopConfirmationRef?.close();
    this.requests.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadJobHistory(): void {
    if (this.historyLoading) return;
    this.historyLoading = true;
    this.historyError = '';
    this.requests.add(
      this.portService
        .getRecentJobs(this.data.target, this.data.targetName)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => (this.historyLoading = false),
          error: () => {
            this.historyLoading = false;
            this.historyError = 'Could not load recent jobs. Please try again.';
          },
        }),
    );
  }

  toggleJobResults(job: NudgePortJob): void {
    if (this.jobResults[job.key]) delete this.jobResults[job.key];
    else this.loadJobResults(job);
  }

  loadJobResults(job: NudgePortJob): void {
    if (this.jobResults[job.key]?.loading) return;
    this.jobResults[job.key] = { loading: true, error: '', links: [] };
    this.requests.add(
      this.portService
        .getJobResult(job)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result) => {
            this.jobResults[job.key] = {
              loading: false,
              error: '',
              links: result.previewLinks,
            };
          },
          error: () => {
            this.jobResults[job.key] = {
              loading: false,
              error: 'Could not load this job’s results. Please try again.',
              links: [],
            };
          },
        }),
    );
  }

  /** Still working: running, or winding down after a Stop. */
  isLive(job: NudgePortJob): boolean {
    return job.status === 'running' || job.status === 'canceling';
  }

  /** Stop already requested — the backend reports `canceling` once it lands. */
  isStopping(job: NudgePortJob): boolean {
    return job.status === 'canceling' || !!job.cancelState;
  }

  /* ------------------------ per-nudge status ---------------------- */

  /** Which nudge rows to show — the detail read supplies them, the list does not. */
  nudgeStatuses(job: NudgePortJob): NudgePortNudgeStatus[] {
    return job.nudgeStatuses || [];
  }

  nudgeStatusLabel(nudge: NudgePortNudgeStatus): string {
    switch (nudge.status) {
      case 'ready':
        return 'Ready';
      case 'partial':
        return 'One device ready';
      case 'error':
        return 'Failed';
      default:
        return 'Waiting';
    }
  }

  /**
   * `testable` means at least one viewport is done, so the nudge already shows
   * in the picker — it does not have to wait for the whole job.
   */
  canTestNudge(nudge: NudgePortNudgeStatus): boolean {
    return !!nudge.testable;
  }

  /**
   * Open one nudge on its page. The job carries links once it has finished;
   * while it is still running they come from the campaign, which is the only
   * read that mints them mid-run.
   */
  testNudge(job: NudgePortJob, nudge: NudgePortNudgeStatus): void {
    const siteId = Number(String(nudge.sourceIncentiveID).split('.')[0]);
    const fromJob = (job.previewLinks || []).find(
      (link) => Number(link.siteID) === siteId,
    );
    if (fromJob) {
      this.openNudgeLink(fromJob.previewUrl, nudge);
      return;
    }
    if (!job.campaignId) return;
    this.testPending = nudge.sourceIncentiveID;
    this.requests.add(
      this.portService
        .getCampaign(job.target, job.campaignId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (campaign) => {
            this.testPending = '';
            const link = (campaign?.previewLinks || []).find(
              (item) => Number(item.siteID) === siteId,
            );
            if (link) {
              this.openNudgeLink(link.previewUrl, nudge);
            }
          },
          error: () => (this.testPending = ''),
        }),
    );
  }

  /**
   * Appends the nudge to the URL the backend minted; never rebuilds it.
   *
   * A port run produces one nudge per source nudge, so there is no viewport to
   * carry: whatever the tool opens at, the nudge asked for is the one there.
   */
  private openNudgeLink(previewUrl: string, nudge: NudgePortNudgeStatus): void {
    if (!previewUrl) return;
    const url =
      previewUrl +
      (previewUrl.includes('?') ? '&' : '?') +
      'nudgeId=' +
      encodeURIComponent(nudge.sourceIncentiveID);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  jobProgress(job: NudgePortJob): number {
    return job.total > 0
      ? Math.min(100, Math.max(0, (job.done / job.total) * 100))
      : 0;
  }

  confirmStopJob(job: NudgePortJob): void {
    if (this.stopConfirmationRef || job.status !== 'running' || job.cancelState)
      return;
    this.stopConfirmationRef = this.dialog.open(ConfirmationComponent, {
      width: '430px',
      maxWidth: '94vw',
      data: {
        header: 'Stop adaptation?',
        content: `Stop adapting nudges for ${job.targetName}? This discards the run — the nudges it has adapted so far are removed.`,
        confirm_btn: 'Stop job',
        cancel_btn: 'Keep running',
        confirmButtonDangerStyle: true,
      },
    });
    this.stopConfirmationRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.stopConfirmationRef = undefined;
        if (result === 1) this.portService.cancel(job);
      });
  }

  jobTitle(job: NudgePortJob): string {
    if (job.status === 'canceling')
      return `Stopping adaptation for ${job.targetName}`;
    if (job.status === 'canceled')
      return `Adaptation canceled for ${job.targetName}`;
    if (job.status === 'error')
      return `Adaptation stopped for ${job.targetName}`;
    if (job.status === 'completed') {
      return job.failed || job.needsReview
        ? `Adaptation finished with items to check for ${job.targetName}`
        : `Your nudges are ready for ${job.targetName}`;
    }
    if (job.cancelState) return `Stopping adaptation for ${job.targetName}`;
    if (job.monitorError)
      return `Checking adaptation progress for ${job.targetName}`;
    return `Adapting ${job.nudges} ${job.nudges === 1 ? 'nudge' : 'nudges'} for ${job.targetName}`;
  }

  jobElapsed(job: NudgePortJob): string {
    const start = Date.parse(job.createdAt || '');
    const end =
      job.status === 'running'
        ? Date.now()
        : Date.parse(job.finishedAt || job.updatedAt || '');
    if (!Number.isFinite(start) || !Number.isFinite(end)) return '';
    const minutes = Math.max(0, Math.floor((end - start) / 60000));
    if (!minutes) return 'Less than a minute';
    if (minutes < 60) return `${minutes} min`;
    return `${Math.floor(minutes / 60)} hr ${minutes % 60} min`;
  }

  trackJob(_index: number, job: NudgePortJob): string {
    return job.key;
  }

  trackJobGroup(_index: number, group: { label: string }): string {
    return group.label;
  }
}
